import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import type { DeckSummary } from '../lib/types'
import { Brand, ThemeToggle, Toast } from '../components/Bits'
import { useAuth } from '../lib/auth'
import { useSeo } from '../lib/seo'
import { GOAL, reachGoal } from '../lib/metrika'
import { slides as slidesLabel, when } from '../lib/plural'

/**
 * «Мои демонстрации» — заготовки презентаций. Здесь только список и действия над
 * ним; сама работа со слайдами — в редакторе демонстрации.
 */
export default function Decks() {
  useSeo({ title: 'Мои демонстрации — clapme', noindex: true })
  const navigate = useNavigate()
  const { user, ready, loginEnabled } = useAuth()
  const [items, setItems] = useState<DeckSummary[] | null>(null)
  const [maxDecks, setMaxDecks] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    api.decks
      .list()
      .then(setItems)
      .catch(() => setItems([]))
  }, [])

  useEffect(() => {
    if (user) load()
  }, [user, load])

  // Сколько демонстраций позволяет тариф — для счётчика рядом с заголовком.
  useEffect(() => {
    if (user) api.billing.me().then((me) => setMaxDecks(me.plan.limits.maxDecks)).catch(() => {})
  }, [user])

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      const deck = await api.decks.create('Новая демонстрация')
      navigate(`/demo/${deck.id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось создать демонстрацию')
    } finally {
      setBusy(false)
    }
  }

  const duplicate = async (deck: DeckSummary) => {
    try {
      await api.decks.duplicate(deck.id)
      load()
    } catch {
      setError('Не получилось продублировать')
    }
  }

  const remove = async (deck: DeckSummary) => {
    if (!window.confirm(`Удалить демонстрацию «${deck.title}»? Слайды вернуть будет нельзя.`)) return
    try {
      await api.decks.remove(deck.id)
      setItems((current) => current?.filter((d) => d.id !== deck.id) ?? null)
    } catch {
      setError('Не получилось удалить')
    }
  }

  if (!ready) return null

  if (!user) {
    return (
      <div className="wrap col" style={{ gap: 20 }}>
        <header className="row spread">
          <Brand />
          <ThemeToggle />
        </header>
        <div className="card col">
          <h2 style={{ margin: 0 }}>Демонстрации доступны после входа</h2>
          <p className="muted" style={{ margin: 0 }}>
            Демонстрация — это презентация, которую вы собираете заранее и запускаете, когда понадобится.
            Войдите, чтобы создавать свои.
          </p>
          {loginEnabled && (
            <a
              className="btn btn-primary"
              href="/api/auth/yandex"
              style={{ textDecoration: 'none' }}
              onClick={() => reachGoal(GOAL.loginClick)}
            >
              Войти через Яндекс
            </a>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="wrap col" style={{ gap: 20, maxWidth: 900 }}>
      <header className="row spread row-wrap">
        <div className="row" style={{ gap: 14 }}>
          <Brand />
          <h1 style={{ fontSize: 24 }}>
            Мои демонстрации
            {items != null && maxDecks != null && (
              <span className="muted tiny">
                {' '}
                · {items.length} из {maxDecks}
              </span>
            )}
          </h1>
        </div>
        <div className="row">
          <button className="btn btn-primary" onClick={create} disabled={busy}>
            {busy ? 'Создаём…' : 'Новая демонстрация'}
          </button>
          <ThemeToggle />
        </div>
      </header>

      {items === null && <p className="muted">Загружаем…</p>}

      {items?.length === 0 && (
        <div className="card col center">
          <h2 style={{ margin: 0 }}>Пока ни одной демонстрации</h2>
          <p className="muted" style={{ margin: 0 }}>
            Соберите презентацию заранее — вопросы, таймеры, правильные ответы. Когда придёт время,
            запустите её в эфир одной кнопкой.
          </p>
        </div>
      )}

      {items?.map((deck) => (
        <div className="card row spread row-wrap" key={deck.id}>
          <Link
            to={`/demo/${deck.id}`}
            className="grow"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <strong>{deck.title}</strong>
            <span className="muted tiny">
              {' '}
              · {slidesLabel(deck.slideCount)} · {when(deck.updatedAt)}
            </span>
          </Link>
          <div className="row">
            <button className="btn" onClick={() => void duplicate(deck)}>
              Дублировать
            </button>
            <button className="btn btn-danger" onClick={() => void remove(deck)}>
              Удалить
            </button>
          </div>
        </div>
      ))}

      <Toast message={error} />
    </div>
  )
}
